/*
 * gpu-zoom: hardware-accelerated detail enhancement for the PDP hover-magnifier.
 *
 * ONLY activates when the browser exposes a real hardware GPU (WebGL present AND the unmasked
 * renderer is not a software rasterizer like SwiftShader / llvmpipe / Microsoft Basic Render).
 * When it does, it overlays a WebGL canvas on the existing .a-gallery__lens and renders the same
 * magnified crop through an unsharp-mask sharpening shader, so the zoomed detail looks crisper.
 *
 * Everything degrades gracefully: no GPU, no WebGL, a CORS-tainted image, or any shader failure
 * removes the canvas and leaves the original CSS background-image lens exactly as it was.
 */
(function () {
  'use strict';

  // ---- 1. Real-GPU detection (skip software renderers) ----
  function detectGpu() {
    try {
      var c = document.createElement('canvas');
      var gl = c.getContext('webgl') || c.getContext('experimental-webgl');
      if (!gl) return false;
      var ext = gl.getExtension('WEBGL_debug_renderer_info');
      var renderer = ext ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || '') : '';
      // If we can read the renderer and it names a software backend, treat as NO gpu.
      var SOFT = /swiftshader|llvmpipe|softpipe|software|basic render|microsoft basic|angle \(software|paravirtual|virgl|mesa offscreen/i;
      if (renderer && SOFT.test(renderer)) return false;
      return true; // real GPU (or renderer masked but WebGL hardware-backed)
    } catch (e) { return false; }
  }
  if (window.__SG_FORCE_GPUZOOM !== true && !detectGpu()) return; // leave the CSS lens as-is

  var Z = 2.5, R = 75, AMOUNT = 0.7, HIRES = 2400; // zoom, lens half (px), CAS sharpness 0..1, texture width

  // a_pos in [0,1] -> clip space. v_uv maps into the crop; texture rows are top-first (no UNPACK
  // flip), and canvas y is bottom-up in clip space, so flip a_pos.y for v_uv to keep top=top.
  var VERT = 'attribute vec2 a_pos; uniform vec2 u_org; uniform vec2 u_size; varying vec2 v_uv;' +
    'void main(){ v_uv = vec2(u_org.x + a_pos.x*u_size.x, u_org.y + (1.0 - a_pos.y)*u_size.y);' +
    ' gl_Position = vec4(a_pos*2.0-1.0, 0.0, 1.0); }';
  // Contrast Adaptive Sharpening (AMD FidelityFX CAS, single pass): edge-aware detail enhancement
  // that lifts fine texture without the halos a plain unsharp mask leaves on high-contrast edges.
  var FRAG = 'precision highp float; uniform sampler2D u_img; uniform vec2 u_texel; uniform float u_amt; varying vec2 v_uv;' +
    'vec3 T(vec2 o){ return texture2D(u_img, v_uv + o*u_texel).rgb; }' +
    'void main(){' +
    ' vec3 a=T(vec2(-1.,-1.)), b=T(vec2(0.,-1.)), c=T(vec2(1.,-1.));' +
    ' vec3 d=T(vec2(-1.,0.)),  e=T(vec2(0.,0.)),  f=T(vec2(1.,0.));' +
    ' vec3 g=T(vec2(-1.,1.)),  h=T(vec2(0.,1.)),  ii=T(vec2(1.,1.));' +
    ' vec3 mn=min(min(min(d,e),min(f,b)),h); mn+=min(mn,min(min(a,c),min(g,ii)));' +
    ' vec3 mx=max(max(max(d,e),max(f,b)),h); mx+=max(mx,max(max(a,c),max(g,ii)));' +
    ' vec3 amp=sqrt(clamp(min(mn, 2.0-mx)/max(mx, vec3(1e-4)), 0.0, 1.0));' +
    ' vec3 w = amp * (-1.0/mix(8.0, 5.0, u_amt));' +
    ' vec3 res = ((b+d+f+h)*w + e) / (1.0 + 4.0*w);' +
    ' gl_FragColor = vec4(clamp(res, 0.0, 1.0), 1.0); }';

  function hires(u) {
    // pull a sharper source than the 1600w CSS-lens image so the magnified crop has real detail
    try {
      if (/[?&]width=\d+/.test(u)) return u.replace(/([?&]width=)\d+/, '$1' + HIRES);
      return u + (u.indexOf('?') >= 0 ? '&' : '?') + 'width=' + HIRES;
    } catch (e) { return u; }
  }

  function compile(gl, type, src) {
    var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || 'shader');
    return s;
  }

  function setupLens(lens, viewport) {
    var canvas = document.createElement('canvas');
    canvas.className = 'a-gallery__lens-gl'; canvas.setAttribute('aria-hidden', 'true');
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(R * 2 * dpr); canvas.height = Math.round(R * 2 * dpr);
    var gl = canvas.getContext('webgl', { premultipliedAlpha: false, antialias: false });
    if (!gl) return;
    var prog, loc, buf, tex;
    try {
      prog = gl.createProgram();
      gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT));
      gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAG));
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error('link');
      gl.useProgram(prog);
      loc = {
        a_pos: gl.getAttribLocation(prog, 'a_pos'), u_org: gl.getUniformLocation(prog, 'u_org'),
        u_size: gl.getUniformLocation(prog, 'u_size'), u_texel: gl.getUniformLocation(prog, 'u_texel'),
        u_amt: gl.getUniformLocation(prog, 'u_amt'), u_img: gl.getUniformLocation(prog, 'u_img')
      };
      buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]), gl.STATIC_DRAW);
      gl.enableVertexAttribArray(loc.a_pos); gl.vertexAttribPointer(loc.a_pos, 2, gl.FLOAT, false, 0, 0);
      tex = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.uniform1i(loc.u_img, 0); gl.uniform1f(loc.u_amt, AMOUNT);
    } catch (e) { return; } // shader/program failed -> CSS lens stays

    lens.appendChild(canvas);
    lens.classList.add('a-gallery__lens--gpu');

    var imgCache = {}, curUrl = null, imgW = 1, imgH = 1, ready = false;
    function urlFromLens() {
      // Alpine sets background-image:url("<zoom src>") on the lens; that's the exact hi-res source.
      var m = /url\(["']?(.*?)["']?\)/.exec(lens.style.backgroundImage || '');
      return m ? m[1] : null;
    }
    function loadTex(url) {
      if (imgCache[url]) { applyTex(imgCache[url]); return; }
      var img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = function () { imgCache[url] = img; if (curUrl === url) applyTex(img); };
      img.onerror = function () { destroy(); }; // e.g. CORS -> give the lens back to CSS
      img.src = hires(url);
    }
    function applyTex(img) {
      try {
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false); // rows top-first; vertex shader handles orientation
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, img);
        imgW = img.naturalWidth || img.width; imgH = img.naturalHeight || img.height; ready = true;
      } catch (e) { destroy(); } // tainted canvas / upload error -> fall back
    }
    function destroy() {
      try { canvas.remove(); } catch (e) {}
      lens.classList.remove('a-gallery__lens--gpu');
      viewport.removeEventListener('mousemove', onMove); disabled = true;
    }

    var disabled = false;
    function onMove(e) {
      if (disabled) return;
      var url = urlFromLens();
      if (!url) return;
      if (url !== curUrl) { curUrl = url; ready = false; loadTex(url); }
      if (!ready) return;
      var r = viewport.getBoundingClientRect();
      if (r.width < 220 || r.height < 220) return;
      var cx = Math.min(r.width - R, Math.max(R, e.clientX - r.left));
      var cy = Math.min(r.height - R, Math.max(R, e.clientY - r.top));
      var halfU = R / (r.width * Z), halfV = R / (r.height * Z);
      var u = cx / r.width, v = cy / r.height;
      var ox = Math.min(1 - 2 * halfU, Math.max(0, u - halfU));
      var oy = Math.min(1 - 2 * halfV, Math.max(0, v - halfV));
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.useProgram(prog);
      gl.uniform2f(loc.u_org, ox, oy);
      gl.uniform2f(loc.u_size, 2 * halfU, 2 * halfV);
      // one output-pixel step in source texel space, for the sharpen neighbourhood
      gl.uniform2f(loc.u_texel, (2 * halfU) / canvas.width, (2 * halfV) / canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }
    viewport.addEventListener('mousemove', onMove, { passive: true });
  }

  function init() {
    var lenses = document.querySelectorAll('.a-gallery__lens');
    for (var i = 0; i < lenses.length; i++) {
      var lens = lenses[i];
      var vp = lens.closest('.a-gallery__viewport');
      if (vp) setupLens(lens, vp);
    }
  }
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
